import { z } from 'zod';
import { PositionSchema } from './squad.ts';

/**
 * One point of difference between the signed-in manager's squad and a
 * tracked rival's: a player one side owns and the other does not, carrying
 * the same composite `score` (from `scorePlayer`) and fixture outlook used
 * everywhere else, so differentials can be ranked by consequence rather than
 * listed alphabetically.
 */
export const Differential = z.object({
  fplId: z.number().int().positive().optional(),
  name: z.string(),
  position: PositionSchema,
  club: z.string(),
  /** Which side owns this player. */
  side: z.enum(['own', 'rival']),
  score: z.number().min(0).max(100),
  /** Mean difficulty of this player's next few fixtures; the neutral value
   * (3) when there are none. */
  fixtureOutlook: z.number(),
});
export type Differential = z.infer<typeof Differential>;

/** A player both squads own. Named only, not scored — being held in common
 * isn't a differential. */
export const SharedPlayer = z.object({
  fplId: z.number().int().positive().optional(),
  name: z.string(),
});
export type SharedPlayer = z.infer<typeof SharedPlayer>;

/**
 * The gap in each side's summed player season points, stated with a
 * direction rather than a bare number — "42 points behind" reads clearly,
 * "42" on its own does not say which way.
 */
export const PointsGap = z.object({
  points: z.number().int().min(0),
  direction: z.enum(['ahead', 'behind', 'level']),
});
export type PointsGap = z.infer<typeof PointsGap>;

/**
 * A comparison of the signed-in manager's loaded squad against one tracked
 * rival's current picks. `ownOnly` and `rivalOnly` are each ranked by score,
 * most consequential difference first; `shared` is what both squads hold in
 * common, which the comparison itself never ranks or scores.
 */
export const RivalComparison = z.object({
  entryId: z.number().int().positive(),
  teamName: z.string(),
  managerName: z.string(),
  pointsGap: PointsGap,
  /** Players the rival owns that the signed-in manager does not. */
  rivalOnly: z.array(Differential),
  /** Players the signed-in manager owns that the rival does not. */
  ownOnly: z.array(Differential),
  shared: z.array(SharedPlayer),
});
export type RivalComparison = z.infer<typeof RivalComparison>;
